import Images from "../../../../../Images"
import { countryCodes, countryShortName } from "../../../../../Utils/countryCodes"
import { disputesColumnIconMap } from "../../../../../Utils/utils"
import { NOT_FIGHTING } from "./statusMeta"

// Static lookup data for the /recover/all disputes table: the option lists behind the
// filter chips, the label/colour maps the columns render, and the small pure helpers
// that read them. Nothing here depends on component state, so it lives beside the page
// rather than inside it.

// Network lifecycle stage, kept SEPARATE from the Chargeback status. Status is
// "what action is needed on our side"; Stage is the card-network cycle the
// dispute sits in. A dispute starts in the first cycle, can be re-opened into
// pre-arbitration, then escalated to arbitration where the network decides.
export const STAGE_LABELS = {
  first_cycle: "First cycle",
  pre_arbitration: "Pre-arbitration",
  arbitration: "Arbitration"
}
export const STAGE_TAG_COLORS = {
  first_cycle: "default",
  pre_arbitration: "orange",
  arbitration: "red"
}
export const STAGE_DESCRIPTIONS = {
  first_cycle: "The first chargeback cycle. You can submit evidence to challenge it.",
  pre_arbitration:
    "The issuer re-opened the dispute after your first response, a second evidence cycle. Whether you can respond depends on your processor.",
  arbitration: "The card network makes the final ruling. No further evidence cycles."
}
export const LIFECYCLE_EXPLANATION =
  "A dispute moves through network lifecycle stages: First cycle (the initial chargeback you respond to), then Pre-arbitration if the issuer re-opens it, then Arbitration where the network decides. This is separate from the Status, which tracks the action needed on your side."
export const stageOf = (cycle_stage) =>
  cycle_stage === "arbitration"
    ? "arbitration"
    : cycle_stage === "pre_arbitration"
      ? "pre_arbitration"
      : "first_cycle"
// Stage filter options, straight from the label map so chip and column can't drift.
export const STAGE_DATA = Object.entries(STAGE_LABELS).map(([id, name]) => ({ id, name }))

// Why a dispute is held for manual review (vs auto-approved). `auto_approve_disabled`
// and `ok` are not shown: the former is a global setting (not a per-dispute reason),
// the latter means it auto-approved (nothing to flag).
export const FLAG_REASON_META = {
  repeat_disputer: { label: "Repeat disputer", color: "red" },
  amount_above_limit: { label: "Above limit", color: "orange" },
  below_win_likelihood: { label: "Low win-likelihood", color: "orange" },
  reason_code_always_manual: { label: "Always-manual category", color: "orange" },
  auto_approve_disabled: null,
  ok: null
}

// Flag filter options: only the reasons that actually render as a flag (null meta
// entries are not per-dispute reasons).
export const FLAG_REASON_DATA = Object.entries(FLAG_REASON_META)
  .filter(([, meta]) => meta)
  .map(([id, meta]) => ({ id, name: meta.label }))

export const FLAG_REASON_DESCRIPTIONS = {
  repeat_disputer: "Held for your review because this customer has disputed before.",
  amount_above_limit: "Held for your review because the amount is above your auto-approve limit.",
  below_win_likelihood:
    "Held for your review because the predicted win likelihood is below your threshold.",
  reason_code_always_manual:
    "Held for your review because this category is set to always review manually."
}

export const CARD_TO_STATUSES = {
  all: [],
  needs_response: ["needs_response"],
  awaiting_review: ["awaiting_review"],
  won: ["won"],
  lost: ["lost_defended", "lost_undefended"],
  pending_decision: ["pending_decision"],
  // Disputes we are not fighting (no representment submitted). Shares the NOT_FIGHTING
  // set with the Analytics Sankey so the card filter, the Sankey node, and the drill all
  // reference one constant. Both v1's and v2's card now resolve to the same Skipped status.
  not_fighting: NOT_FIGHTING,
  // v2: one bucket for everything finished. Won and the two lost outcomes finish a case.
  closed: ["won", "lost_defended", "lost_undefended"],
  // v2: "Not fighting" is the Skipped status (ignore-low-value rule + Stop fighting),
  // the same set v1's card of the same name uses.
  skipped: ["skipped"]
}

// v2 cards that are NOT status filters. Each maps to its own list filter param instead,
// and both need a real backend equivalent (see recoverDemo.js's statistics note).
// The "low enrichment" cut-off, as a percentage. A product decision, so it lives in one
// place. (Previously imported from the playground; this page should not depend on
// removable scaffolding for a real product constant.)
export const LOW_ENRICHMENT_MAX = 10
// Thin evidence only counts where it can still be acted on: a resolved or already-submitted
// dispute cannot be enriched any more, so listing it under "Low enrichment" is busywork.
export const LOW_ENRICHMENT_STATUSES = ["needs_response", "awaiting_review"]
export const CARD_TO_FILTERS = {
  // A preset of the Enrichment filter, not a param of its own, so the filter chip shows
  // the card's selection and clearing either one clears both. `is_between` 0..max is how
  // "at or below max" is expressed: the number filter has no "or equal" type, and
  // is_between is inclusive at both ends (is_less_than would exclude the threshold).
  low_enrichment: {
    status: LOW_ENRICHMENT_STATUSES,
    enrichment: { type: "is_between", min: 0, max: LOW_ENRICHMENT_MAX }
  },
  // Everything awaiting an automatic send. Reuses the existing Submission "scheduled"
  // bucket rather than a bespoke param, so this card, the Submission column and the
  // Submission filter can never disagree. That bucket is Open plus Reopened, which are
  // exactly the rows that render "Scheduled to be sent".
  scheduled: { submission: ["scheduled"] }
}
// Does this dispute have an automatic send pending? Same rule the Submission column
// renders ("Scheduled to be sent"): an Open dispute with a send date, explicit or
// derived from the response deadline.
export const isScheduledSend = (r) =>
  r?.status === "needs_response" && !!(r?.scheduled_send_at || r?.due_by)

// Counts for the three action cards, derived from a record set. Analytics has the full
// dispute set in memory and computes them here; the list page gets the same numbers from
// the backend's `statistics` block (it is paginated, so it cannot count locally).
export const computeCardCounts = (records = []) => {
  let awaitingReview = 0
  let lowEnrichment = 0
  let scheduled = 0
  for (const r of records) {
    if (r?.status === "awaiting_review") awaitingReview += 1
    if (
      LOW_ENRICHMENT_STATUSES.includes(r?.status) &&
      Number(r?.enrichment) <= LOW_ENRICHMENT_MAX
    ) {
      lowEnrichment += 1
    }
    if (isScheduledSend(r)) scheduled += 1
  }
  return { awaiting_review: awaitingReview, low_enrichment: lowEnrichment, scheduled }
}

// Is the Enrichment filter currently set to exactly the "Low enrichment" preset? A range
// the merchant typed themselves must NOT light the card up.
export const isLowEnrichmentPreset = (v) =>
  !!v && v.type === "is_between" && Number(v.min) === 0 && Number(v.max) === LOW_ENRICHMENT_MAX

export const ISSUER_DATA = [
  { id: "visa", name: "Visa" },
  { id: "mastercard", name: "Mastercard" },
  { id: "amex", name: "Amex" },
  { id: "discover", name: "Discover" }
]
// Issuing banks (the cardholder's bank) for the "Issuer" column/filter. Distinct from
// the card network above; matches the demo's issuing_bank values.
export const ISSUER_BANK_DATA = [
  "Chase",
  "Bank of America",
  "Capital One",
  "Citi",
  "Wells Fargo",
  "US Bank",
  "Barclays",
  "HSBC"
].map((b) => ({ id: b, name: b }))
export const CARD_TYPE_DATA = [
  { id: "credit", name: "Credit" },
  { id: "debit", name: "Debit" }
]
export const CURRENCY_DATA = ["USD", "GBP", "EUR"].map((c) => ({ id: c, name: c }))
// Submission filter options: the event-type buckets the Submission column renders
// (derived from status, same mapping as filterRecoverDemo's submission filter).
export const SUBMISSION_DATA = [
  { id: "scheduled", name: "Scheduled to be sent" },
  { id: "needs_review", name: "Needs manual review" },
  { id: "sent", name: "Sent" },
  // Stopping the fight cancels the scheduled send; filterable like the other events.
  { id: "canceled", name: "Submission canceled" }
]
// Short, common country names (alpha-2 -> name) for the Country column/filter, e.g.
// "United Kingdom" rather than the long ISO form. countryShortName falls back to the
// ISO name for codes without a short override.
export const COUNTRY_NAMES = countryCodes.reduce((acc, c) => {
  if (c["alpha-2"]) acc[c["alpha-2"]] = countryShortName(c["alpha-2"])
  return acc
}, {})
export const COUNTRY_DATA = Object.entries(COUNTRY_NAMES).map(([id, name]) => ({ id, name }))
// Only the two processors the demo account has connected. `flag` renders the logo in
// the dropdown row, the same mechanism the Alerts filters use for processor logos.
export const PROCESSOR_DATA = [
  { id: "stripe", name: "Stripe", flag: Images.integration_stripe },
  { id: "shopify", name: "Shopify", flag: Images.integration_shopify }
]
// The reason codes present in the data, compact labels for the filter dropdown (the
// full network sentences live in CODE_LABELS on the detail page).
export const REASON_CODE_DATA = [
  { id: "10.3", name: "10.3 · Other Fraud, Card-Present" },
  { id: "10.4", name: "10.4 · Other Fraud, Card-Absent" },
  { id: "12.6", name: "12.6 · Duplicate Processing" },
  { id: "13.1", name: "13.1 · Merchandise Not Received" },
  { id: "13.2", name: "13.2 · Canceled Recurring" },
  { id: "13.3", name: "13.3 · Not as Described / Defective" },
  { id: "13.6", name: "13.6 · Credit Not Processed" }
]

// Columns the backend can order by (recover/views.py DisputeListView whitelist).
export const SORTABLE = new Set([
  "number",
  "due_by",
  "dispute_created_at",
  "updated_at",
  "amount",
  "status",
  "customer_full_name",
  "customer_email",
  "transaction_date",
  "reason_category",
  "reason_code",
  "issuer",
  "issuing_bank",
  "card_type",
  "currency",
  "processor_dispute_id",
  "processor",
  "country",
  "card_last4",
  "cycle_stage"
])

// Reserved for any column whose key differs from its backend ordering field.
// The # column now renders the dispute's real per-account `number`, so it sorts
// on that field directly (no remap needed).
export const SORT_FIELD_BY_KEY = {}

// Per-column header icon. Shared with the Edit-columns dropdown (ManageColumns
// iconsType="disputes") so the two stay in sync. Registry names.
export const COLUMN_ICONS = disputesColumnIconMap

// Every date-range chip and the filter pair it writes, keyed by its FILTERS key.
// `id` is the DropdownDateBlock id (its emitted payload key). Adding a date chip
// means one entry here plus the registry row.
export const DATE_PARAMS = {
  date: { id: "date_by", from: "date_from", to: "date_to" },
  updated: { id: "updated_by", from: "updated_from", to: "updated_to" },
  transaction_date: { id: "transaction_by", from: "transaction_from", to: "transaction_to" },
  due_by: { id: "due_by", from: "due_from", to: "due_to" }
}

// Card-brand logos for the Issuer column. These are full-colour registry tiles
// (visa/mastercard/americanexpress/discover). Anything unmapped falls back to
// the plain capitalised name.
export const ISSUER_ICON = {
  visa: "visa",
  mastercard: "mastercard",
  amex: "americanexpress",
  discover: "discover"
}

// Processor logos reuse the existing integration image assets (same ones the
// processor filter uses), shown at 20px. Looked up as Images[`integration_<id>`].
export const processorImage = (id) => (id ? Images[`integration_${id}`] : null)

// Deep-link a row to the disputed transaction in the connected processor's
// dashboard. Stripe opens the payment itself; other processors fall back to
// whatever transaction_url the backend resolved (null keeps the cell plain text).
export const transactionUrl = (row) => {
  if (!row) return null
  if (row.processor === "stripe" && row.processor_charge_id) {
    return `https://dashboard.stripe.com/payments/${row.processor_charge_id}`
  }
  return row.transaction_url || null
}

export const pageSize = 50

const CHECKBOX_FILTER_KEYS = [
  "status",
  "reason_category",
  "reason_code",
  "flag_reason",
  "cycle_stage",
  "issuer",
  "issuing_bank",
  "customer",
  "customer_email",
  "assignee",
  "card_type",
  "currency",
  "country",
  "processor",
  "card_last4",
  "processor_dispute_id"
]

// Has the merchant narrowed the list at all? Read by the page (which hides the metric
// cards and the filter strip on a genuinely empty account) and by the filter strip
// itself (which only offers "Reset filters" once something is applied).
export const isFiltersDirty = (filters) =>
  CHECKBOX_FILTER_KEYS.some((k) => (filters[k]?.length || 0) > 0) ||
  !!filters.date_from ||
  !!filters.date_to ||
  !!filters.updated_from ||
  !!filters.updated_to ||
  !!filters.due_from ||
  !!filters.due_to ||
  !!filters.transaction_from ||
  !!filters.transaction_to ||
  !!filters.amount?.type ||
  // The Enrichment range and the Submission bucket count as narrowing too, so "All"
  // never highlights while one of them is applied.
  !!filters.enrichment?.type ||
  (filters.submission || []).length > 0
